import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback, useMemo } from 'react';
import { api } from '../api/client';
import { Tier } from '../utils/tier-constants';

interface User {
  userId: string;
  email?: string;
  tier: Tier;
  premiumExpiresAt?: number;
  telegramId?: number;
  telegramDisplayName?: string;
  emailVerified: boolean;
  createdAt: number;
  updatedAt: number;
  isAdmin?: boolean;
  devices?: Record<string, any>;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  telegramLogin: (authData: any) => Promise<void>;
  register: (email: string, password: string) => Promise<void>;
  registerMagic: (email: string) => Promise<void>;
  logout: () => void;
  loadUser: () => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}



export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const normalizeUser = (userData: any): User => ({
    ...userData,
    tier: userData.tier ? (userData.tier.toLowerCase() as Tier) : 'free'
  });

  const loadUser = useCallback(async () => {
    try {
      const response = await api.get('/user/profile');
      setUser(normalizeUser(response.data));
    } catch (error) {
      console.error('Failed to load user:', error);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, []);


  // Initial load
  useEffect(() => {
    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for unauthorized events from global API client
  useEffect(() => {
    const handleUnauthorized = () => {
      console.warn('Authentication failed globally, logging out user');
      setUser(null);
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await api.post('/user/login', {
        email,
        password,
      });
      
      setUser(normalizeUser(response.data.user));
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Login failed');
    }
  }, []);

  const telegramLogin = React.useCallback(async (authData: any) => {
    try {
      const response = await api.post('/user/telegram-login', authData);
      setUser(normalizeUser(response.data.user));
    } catch (error: any) {
      console.error('Telegram login error in context:', error);
      throw new Error(error.response?.data?.message || 'Telegram Login failed');
    }
  }, []);

  const register = useCallback(async (email: string, password: string) => {
    try {
      const response = await api.post('/user/register', {
        email,
        password,
      });
      
      setUser(normalizeUser(response.data.user));
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Registration failed');
    }
  }, []);

  const registerMagic = useCallback(async (email: string) => {
    try {
      const response = await api.post('/user/register-magic', { email });
      setUser(normalizeUser(response.data.user));
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Registration failed');
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/user/logout', {});
    } catch (err) {
      console.warn('Logout request failed', err);
    }
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user,
    login,
    telegramLogin,
    register,
    registerMagic,
    logout,
    loadUser,
    isAuthenticated: !!user,
    isLoading,
  }), [user, login, telegramLogin, register, registerMagic, logout, loadUser, isLoading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
